/**
 * Alerts Screen — A3-S2 / A3-S3
 *
 * Left column: live alert stream from Core WebSocket with status count strip,
 * filter tabs, and alert rows (severity colour bar, type, zone, condition,
 * status badge, elapsed timer).
 *
 * Right column: alert detail — severity badge, lifecycle stepper, trigger
 * context, zone snapshot, Core history, resolution note textarea (supervisors),
 * resolved state display, and tabbed Detail / History view.
 *
 * Lifecycle actions wire to Core REST with authoritative state refresh. Errors
 * surface as toast notifications; success also shows a toast confirmation.
 *
 * Alert stream and shell count are owned by AppContext for the selected Operation.
 *
 * No PII — all values are zone names and anonymous density aggregates.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Alert, AlertStatus, DensitySnapshot } from '@vigilo/shared';
import { useApp } from '@/shell/AppContext';
import { alertActorLabel, type AlertActorUser } from '../utils/alert-actor';
import { useMockMode } from '@/shell/MockModeContext';
import {
  type ColorPalette, Icons, KLabel, StatusDot, Pill, Panel, Btn, SeverityColor,
  FONT_SIZE, FONT_WEIGHT, LETTER_SPACING, LINE_HEIGHT, RADIUS, SPACE, SIZE,
} from '@vigilo/design-system';
import { getCrowdC, operationStatusColor } from '../../design/crowdPalette';
import { useDensityData } from '../data/useMockData';
import { useAlertHistory } from '../hooks/useAlertHistory';
import { useZones } from '../hooks/useZones';
import {
  CROWD_CLIENT_ACTIVITY_ACTION,
  CROWD_CLIENT_RESOURCE_TYPE,
  useCrowdActivityAudit,
  useCrowdFilterActivity,
} from '../hooks/useCrowdActivityAudit';
import { isZoneMetricFresh, useLiveZoneMetrics, type CurrentZoneMetrics } from '../hooks/useLiveZoneMetrics';
import { useToast, ToastContainer } from '../components/Toast';
import { PaginationControls } from '../components/PaginationControls';
import { usePagedAlerts, type PagedAlertFilterStatus } from '../hooks/usePagedAlerts';

// ── Colour helpers ────────────────────────────────────────────────────────────

function alertSevColor(severity: string, C: ColorPalette): string {
  if (severity === 'informational') return C.teal;
  return SeverityColor(C, severity);
}

function alertStatusColor(status: AlertStatus, C: ColorPalette): string {
  const map: Record<AlertStatus, string> = {
    open:         C.ember,
    acknowledged: C.amber,
    investigating: C.amber,
    contained:    C.amber,
    escalated:    C.critical,
    resolved:     C.green,
  };
  return map[status] ?? C.muted;
}

// ── Formatters ────────────────────────────────────────────────────────────────

function formatType(type: string): string {
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatElapsed(iso: string): string {
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  const s = secs % 60;
  const m = Math.floor(secs / 60) % 60;
  const h = Math.floor(secs / 3600);
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function truncateId(id: string): string {
  return id.length > 10 ? `${id.slice(0, 8)}…` : id;
}

function formatAlertDisplayId(alert: Alert): string {
  const d = new Date(alert.triggeredAt);
  const stamp = d.toLocaleString([], {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  return `Alert · ${stamp}`;
}

// ── Status count chip ─────────────────────────────────────────────────────────

function alertZoneDisplayName(alert: Alert, operationZoneNameById: ReadonlyMap<string, string>): string {
  const selectedOperationZoneName = operationZoneNameById.get(alert.zoneId)?.trim();
  if (selectedOperationZoneName) return selectedOperationZoneName;

  const projectedZoneName = alert.zoneName.trim();
  return projectedZoneName || alert.zoneId;
}

function alertTriggerProjection(alert: Alert, operationId: string): Alert['triggerContext'] | null {
  return alert.operationId === operationId ? alert.triggerContext : null;
}

function formatTriggerCondition(context: Alert['triggerContext'] | null | undefined): string {
  const condition = context?.condition?.trim();
  return condition ? condition : 'Not returned';
}

function formatTriggerMetric(value: number | null | undefined, unit: string | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'Not returned';
  const suffix = unit?.trim();
  return suffix ? `${value} ${suffix}` : `${value}`;
}

type ZoneSnapshotStatus = 'simulation' | 'fresh' | 'stale' | 'missing' | 'loading' | 'error';

const RESOLUTION_NOTE_MIN_LENGTH = 10;
const RESOLUTION_NOTE_MAX_LENGTH = 500;
const DEFAULT_RESOLUTION_NOTE = 'Resolved in Crowd Alerts without additional note.';

function resolutionNoteError(note: string): string | null {
  const length = note.trim().length;
  if (length === 0) return null;
  if (length < RESOLUTION_NOTE_MIN_LENGTH) return `Resolution note must be at least ${RESOLUTION_NOTE_MIN_LENGTH} characters.`;
  if (length > RESOLUTION_NOTE_MAX_LENGTH) return `Resolution note must be ${RESOLUTION_NOTE_MAX_LENGTH} characters or fewer.`;
  return null;
}

function metricToDensitySnapshot(metric: CurrentZoneMetrics): DensitySnapshot {
  return {
    zoneId: metric.zoneId,
    occupancyCount: metric.occupancyCount,
    densityScore: metric.densityScore,
    capacityUtilisation: metric.capacityUtilisation,
    trendDirection: metric.trendDirection,
    trendRate: metric.trendRate,
    timestamp: metric.lastEventAt,
  };
}

function zoneSnapshotStatus(
  isSimulation: boolean,
  returnedMetric: CurrentZoneMetrics | null,
  loading: boolean,
  error: string | null,
): ZoneSnapshotStatus {
  if (isSimulation) return 'simulation';
  if (error) return 'error';
  if (returnedMetric && isZoneMetricFresh(returnedMetric)) return 'fresh';
  if (returnedMetric) return 'stale';
  if (loading) return 'loading';
  return 'missing';
}

function zoneSnapshotStatusLabel(status: ZoneSnapshotStatus): string {
  if (status === 'simulation') return 'SIMULATION';
  if (status === 'fresh') return 'LIVE';
  if (status === 'stale') return 'STALE';
  if (status === 'loading') return 'LOADING';
  if (status === 'error') return 'UNAVAILABLE';
  return 'NO PROJECTION';
}

function zoneSnapshotStatusColor(status: ZoneSnapshotStatus, C: ColorPalette): string {
  if (status === 'fresh' || status === 'simulation') return C.green;
  if (status === 'error') return C.ember;
  if (status === 'stale' || status === 'missing') return C.amber;
  return C.muted;
}

function alertStreamStatusLabel(status: string, source: string): string {
  if (status === 'requires_operation') return 'Operation required';
  if (status === 'disabled') return 'Alert stream disabled';
  if (status === 'connected') return source === 'mock' ? 'Mock stream active' : 'Live feed connected';
  if (status === 'fallback') return 'Simulation stream active';
  if (status === 'stale') return 'Alert stream stale';
  if (status === 'disconnected') return 'Alert stream disconnected';
  return 'Connecting to live feed';
}

function alertStreamStatusColor(status: string, C: ColorPalette): string {
  if (status === 'connected' || status === 'fallback') return C.green;
  if (status === 'stale' || status === 'requires_operation') return C.amber;
  if (status === 'disconnected') return C.ember;
  return C.muted;
}

function formatStreamSyncTime(iso: string | null): string {
  if (!iso) return 'No sync yet';
  return `Last sync ${formatTime(iso)}`;
}

function CountChip({ label, value, color, C }: { label: string; value: number | null; color: string; C: ColorPalette }) {
  return (
    <div style={{
      background: C.surface, border: `1px solid ${C.border}`,
      borderLeft: `${SIZE.severityBar}px solid ${color}`,
      borderRadius: RADIUS.md, padding: `${SPACE[6]}px ${SPACE[8]}px`,
    }}>
      <div style={{
        fontSize: FONT_SIZE.label, color: C.muted, fontWeight: FONT_WEIGHT.bold,
        letterSpacing: LETTER_SPACING.field, textTransform: 'uppercase',
      }}>{label}</div>
      <div style={{
        fontSize: FONT_SIZE.heading, fontWeight: FONT_WEIGHT.bold,
        color: C.text, lineHeight: LINE_HEIGHT.chip,
      }}>{value === null ? 'Unavailable' : value}</div>
    </div>
  );
}

// ── Alert row ─────────────────────────────────────────────────────────────────

function AlertRow({ alert, zoneDisplayName, isSelected, onClick, C }: {
  alert: Alert; zoneDisplayName: string; isSelected: boolean; onClick: () => void; C: ColorPalette;
}) {
  const sevColor = alertSevColor(alert.severity, C);
  const stColor  = alertStatusColor(alert.status, C);

  return (
    <div
      onClick={onClick}
      style={{
        padding: `${SPACE[12]}px ${SPACE[18]}px`,
        borderBottom: `1px solid ${C.border}`,
        background:   isSelected ? C.faint : 'transparent',
        borderLeft:   `${SIZE.severityBar}px solid ${isSelected ? sevColor : 'transparent'}`,
        cursor:       'pointer',
        transition:   'all 120ms',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: SPACE[4] }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[6] }}>
          <StatusDot color={sevColor} pulse={alert.status === 'open'} size={6} />
          <span style={{
            fontSize: FONT_SIZE.base, fontWeight: FONT_WEIGHT.bold, color: sevColor,
            letterSpacing: LETTER_SPACING.wide, textTransform: 'uppercase',
          }}>
            {formatType(alert.alertType)}
          </span>
        </div>
        <span style={{ fontSize: FONT_SIZE.micro, fontFamily: 'monospace', color: C.muted }}>
          {formatTime(alert.triggeredAt)}
        </span>
      </div>

      <div style={{
        fontSize: FONT_SIZE.body, fontWeight: FONT_WEIGHT.semibold,
        color: C.text, marginBottom: SPACE[3],
      }}>
        {zoneDisplayName}
      </div>
      <div style={{
        fontSize: FONT_SIZE.sm, color: C.muted,
        marginBottom: SPACE[6], lineHeight: LINE_HEIGHT.relaxed,
      }}>
        {formatTriggerCondition(alert.triggerContext)}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{
          fontSize: FONT_SIZE.label, fontWeight: FONT_WEIGHT.bold, color: stColor,
          letterSpacing: LETTER_SPACING.label, textTransform: 'uppercase',
          background: `${stColor}22`, padding: `2px ${SPACE[6]}px`, borderRadius: RADIUS.sm,
        }}>
          {alert.status}
        </span>
        <span style={{ fontSize: FONT_SIZE.micro, color: C.muted, fontFamily: 'monospace' }}>
          {formatElapsed(alert.triggeredAt)} elapsed
        </span>
      </div>
    </div>
  );
}

// ── Lifecycle stepper ─────────────────────────────────────────────────────────

const LIFECYCLE: AlertStatus[] = ['open', 'acknowledged', 'escalated', 'resolved'];

function stageColor(stage: string, C: ColorPalette): string {
  const map: Record<string, string> = {
    open: C.ember, acknowledged: C.amber, escalated: C.critical, resolved: C.green,
  };
  return map[stage] ?? C.muted;
}

function stageTs(stage: AlertStatus, alert: Alert): string | null {
  if (stage === 'open')         return alert.triggeredAt;
  if (stage === 'acknowledged') return alert.acknowledgedAt;
  if (stage === 'escalated')    return alert.escalatedAt;
  if (stage === 'resolved')     return alert.resolvedAt;
  return null;
}

// The officer behind a step, shown under its time. Automatic escalation is the system's, so it carries no officer.
function stageActor(stage: AlertStatus, alert: Alert, currentUser: AlertActorUser): string | null {
  if (stage === 'acknowledged' && alert.acknowledgedAt && alert.acknowledgedBy && alert.acknowledgedBy !== 'system') {
    return alertActorLabel(alert.acknowledgedBy, currentUser);
  }
  if (stage === 'resolved' && alert.resolvedAt && alert.resolvedBy) return alertActorLabel(alert.resolvedBy, currentUser);
  return null;
}

function LifecycleStepper({ alert, C, currentUser }: { alert: Alert; C: ColorPalette; currentUser: AlertActorUser }) {
  const currentIdx = LIFECYCLE.indexOf(alert.status);

  return (
    <Panel C={C} style={{ padding: SPACE[18], marginBottom: SPACE[16] }}>
      <KLabel C={C}>Lifecycle</KLabel>
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {LIFECYCLE.map((stage, i) => {
          const isDone   = i < currentIdx;
          const isActive = i === currentIdx;
          const color    = stageColor(stage, C);
          const ts       = stageTs(stage, alert);

          return (
            <Fragment key={stage}>
              <div style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: SPACE[6], minWidth: 0,
              }}>
                <div style={{
                  width: SIZE.stepCircle, height: SIZE.stepCircle, borderRadius: '50%',
                  background: (isDone || isActive) ? color : C.faint,
                  border: `2px solid ${(isDone || isActive) ? color : C.border3}`,
                  color: (isDone || isActive) ? '#fff' : C.muted,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: isActive ? `0 0 0 4px ${color}33` : 'none',
                  fontSize: FONT_SIZE.base, fontWeight: FONT_WEIGHT.bold, flexShrink: 0,
                }}>
                  {isDone ? <Icons.Check size={SIZE.iconMd} /> : i + 1}
                </div>
                <div style={{
                  fontSize: FONT_SIZE.micro, fontWeight: FONT_WEIGHT.bold,
                  letterSpacing: LETTER_SPACING.wide, textTransform: 'uppercase',
                  textAlign: 'center',
                  color: isActive ? color : isDone ? C.sub : C.muted,
                }}>
                  {stage}
                </div>
                <div style={{
                  fontSize: FONT_SIZE.label, fontFamily: 'monospace', color: C.muted,
                  textAlign: 'center', minHeight: SPACE[12],
                }}>
                  {ts ? formatTime(ts) : ''}
                </div>
                <div style={{
                  fontSize: FONT_SIZE.label, color: C.muted, textAlign: 'center', minHeight: SPACE[12],
                }}>
                  {stageActor(stage, alert, currentUser) ?? ''}
                </div>
              </div>
              {i < LIFECYCLE.length - 1 && (
                <div style={{
                  height: 2, flex: 0.4, minWidth: SPACE[12],
                  background: isDone ? color : C.border2,
                  marginTop: SPACE[16],
                }} />
              )}
            </Fragment>
          );
        })}
      </div>
    </Panel>
  );
}

// ── Action log entry ──────────────────────────────────────────────────────────

function ActionEntry({ time, who, action, text, C }: {
  time: string;
  who:  string;
  action: NonNullable<Alert['triggerContext']['activityTrail']>[number]['action'];
  text: string;
  C:    ColorPalette;
}) {
  const color = {
    created: C.ember,
    acknowledged: C.amber,
    escalated: C.critical,
    resolved: C.green,
    reopened: C.amber,
    assigned: C.teal,
    noted: C.teal,
  }[action] ?? C.muted;
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start',
      columnGap: SPACE[14], rowGap: SPACE[6],
      paddingBottom: SPACE[10], marginBottom: SPACE[10],
      borderBottom: `1px solid ${C.border}`,
    }}>
      <div style={{
        width: SIZE.actionLogTime, flexShrink: 0,
        fontSize: FONT_SIZE.sm, fontFamily: 'monospace', color: C.muted, paddingTop: 1,
      }}>
        {time}
      </div>
      <div style={{ flex: '0 0 auto', maxWidth: '100%' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center',
          fontSize: FONT_SIZE.label, fontWeight: FONT_WEIGHT.bold,
          letterSpacing: LETTER_SPACING.label, textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          color, background: `${color}22`, padding: `2px ${SPACE[6]}px`, borderRadius: RADIUS.sm,
        }}>
          {formatType(action)}
        </span>
      </div>
      <div style={{
        flex: '1 1 260px', minWidth: 0,
        fontSize: FONT_SIZE.basePlus, color: C.text, lineHeight: LINE_HEIGHT.relaxed,
        overflowWrap: 'anywhere',
      }}>{text}</div>
      <div style={{
        flex: '0 1 180px', marginLeft: 'auto',
        fontSize: FONT_SIZE.sm, color: C.muted, textAlign: 'right',
        overflowWrap: 'anywhere',
      }}>{who}</div>
    </div>
  );
}

// ── Alert detail ──────────────────────────────────────────────────────────────

type DetailTab = 'details' | 'history';

function liveColor(util: number, C: ColorPalette): string {
  if (util >= 85) return C.ember;
  if (util >= 70) return C.amber;
  return C.green;
}

function AlertDetail({
  alert,
  zoneDisplayName,
  operationId,
  canResolve,
  isSimulation,
  simulationSnapshot,
  selectedZoneMetric,
  returnedZoneMetric,
  zoneMetricsLoading,
  zoneMetricsError,
  onAcknowledge,
  onEscalate,
  onResolve,
  onTabChange,
  C,
}: {
  alert:              Alert;
  zoneDisplayName:    string;
  operationId:        string;
  canResolve:         boolean;
  isSimulation:       boolean;
  simulationSnapshot: DensitySnapshot | null;
  selectedZoneMetric: CurrentZoneMetrics | null;
  returnedZoneMetric: CurrentZoneMetrics | null;
  zoneMetricsLoading: boolean;
  zoneMetricsError:   string | null;
  onAcknowledge:      () => Promise<void>;
  onEscalate:         () => Promise<void>;
  onResolve:          (note: string) => Promise<void>;
  onTabChange:        (tab: DetailTab) => void;
  C:                  ColorPalette;
}) {
  const { currentUser: detailUser } = useApp();
  const sevColor = alertSevColor(alert.severity, C);
  const triggerProjection = alertTriggerProjection(alert, operationId);
  const snap     = alert.zoneSnapshot;
  const snapshotStatus = zoneSnapshotStatus(isSimulation, returnedZoneMetric, zoneMetricsLoading, zoneMetricsError);
  const snapshotStatusColor = zoneSnapshotStatusColor(snapshotStatus, C);
  const currentSnapshot = isSimulation
    ? simulationSnapshot
    : selectedZoneMetric
      ? metricToDensitySnapshot(selectedZoneMetric)
      : null;

  const [tab,         setTab]         = useState<DetailTab>('details');
  const [note,        setNote]        = useState('');
  const [ackLoading,  setAckLoading]  = useState(false);
  const [escLoading,  setEscLoading]  = useState(false);
  const [resLoading,  setResLoading]  = useState(false);

  const history = useAlertHistory(alert.id, operationId, tab === 'history');
  const trimmedResolutionNote = note.trim();
  const noteValidationError = resolutionNoteError(note);
  const canSubmitResolution = !resLoading && noteValidationError === null;

  async function handleAck() {
    setAckLoading(true);
    try { await onAcknowledge(); } finally { setAckLoading(false); }
  }

  async function handleEsc() {
    setEscLoading(true);
    try { await onEscalate(); } finally { setEscLoading(false); }
  }

  async function handleRes() {
    if (noteValidationError) return;
    setResLoading(true);
    try { await onResolve(trimmedResolutionNote || DEFAULT_RESOLUTION_NOTE); setNote(''); } finally { setResLoading(false); }
  }

  return (
    <div>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: SPACE[18], gap: SPACE[12], flexWrap: 'wrap',
      }}>
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: SPACE[10],
            marginBottom: SPACE[6], flexWrap: 'wrap',
          }}>
            <Pill color={sevColor}>{alert.severity}</Pill>
            <span style={{ fontSize: FONT_SIZE.base, color: C.muted }}>{formatAlertDisplayId(alert)}</span>
            <span style={{ fontSize: FONT_SIZE.base, color: C.muted }}>·</span>
            <span style={{ fontSize: FONT_SIZE.base, color: C.muted }}>
              {zoneDisplayName}
            </span>
          </div>
          <div style={{ fontSize: FONT_SIZE.displaySm, fontWeight: FONT_WEIGHT.bold, color: C.text, marginBottom: SPACE[2] }}>
            {formatType(alert.alertType)}
          </div>
          <div style={{ fontSize: FONT_SIZE.body, color: C.muted }}>{formatTriggerCondition(triggerProjection)}</div>
        </div>

        {/* Action buttons — hidden when resolved */}
        {alert.status !== 'resolved' && (
          <div style={{ display: 'flex', gap: SPACE[6], flexWrap: 'wrap', flexShrink: 0 }}>
            {alert.status === 'open' && (
              <Btn
                C={C} variant="primary" size="sm"
                icon={<Icons.Check size={SIZE.iconSm} />}
                onClick={handleAck} disabled={ackLoading}
              >
                {ackLoading ? 'Acknowledging…' : 'Acknowledge'}
              </Btn>
            )}
            {(alert.status === 'open' || alert.status === 'acknowledged') && (
              <Btn
                C={C} variant="danger" size="sm"
                icon={<Icons.Up size={SIZE.iconSm} />}
                onClick={handleEsc} disabled={escLoading}
              >
                {escLoading ? 'Escalating…' : 'Escalate'}
              </Btn>
            )}
          </div>
        )}
      </div>

      {/* ── Lifecycle stepper ────────────────────────────────────────────── */}
      <LifecycleStepper alert={alert} C={C} currentUser={detailUser} />

      {/* ── Tab bar: Details | History ───────────────────────────────────── */}
      <div style={{
        display: 'flex', gap: 0,
        borderBottom: `1px solid ${C.border2}`,
        marginBottom: SPACE[16],
      }}>
        {(['details', 'history'] as DetailTab[]).map(t => (
          <button key={t} onClick={() => {
            setTab(t);
            onTabChange(t);
          }} style={{
            padding: `${SPACE[10]}px ${SPACE[16]}px`,
            fontSize: FONT_SIZE.body, fontWeight: FONT_WEIGHT.semibold,
            fontFamily: "'Albert Sans', sans-serif",
            color: tab === t ? C.text : C.sub, background: 'transparent',
            border: 'none', borderBottom: `2px solid ${tab === t ? C.amber : 'transparent'}`,
            cursor: 'pointer', marginBottom: -1, transition: 'all 120ms',
            textTransform: 'capitalize',
          }}>{t}</button>
        ))}
      </div>

      {tab === 'details' && (
        <>
          {/* ── Trigger context + Zone snapshot ─────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: SPACE[14], marginBottom: SPACE[16] }}>
            <Panel C={C} style={{ padding: SPACE[18] }}>
              <KLabel C={C}>Trigger Context</KLabel>
              {([
                ['Triggered',     formatTime(alert.triggeredAt)],
                ['Time elapsed',  `${formatElapsed(alert.triggeredAt)} ago`],
                ['Condition',     formatTriggerCondition(triggerProjection)],
                ['Value at trigger', formatTriggerMetric(triggerProjection?.currentValue, triggerProjection?.unit)],
                ['Threshold',     formatTriggerMetric(triggerProjection?.thresholdValue, triggerProjection?.unit)],
              ] as [string, string][]).map(([label, value]) => (
                <div key={label} style={{
                  display: 'flex', justifyContent: 'space-between',
                  padding: `${SPACE[6]}px 0`, borderBottom: `1px solid ${C.border}`,
                }}>
                  <span style={{ fontSize: FONT_SIZE.base, color: C.muted }}>{label}</span>
                  <span style={{ fontSize: FONT_SIZE.basePlus, fontWeight: FONT_WEIGHT.semibold, color: C.text }}>{value}</span>
                </div>
              ))}
            </Panel>

            <Panel C={C} style={{ padding: SPACE[18] }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: SPACE[8] }}>
                <KLabel C={C} mb={0}>Zone Snapshot</KLabel>
                {currentSnapshot && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[4] }}>
                    <StatusDot color={snapshotStatusColor} pulse={snapshotStatus === 'fresh' || snapshotStatus === 'simulation'} size={5} />
                    <span style={{
                      fontSize: FONT_SIZE.label, fontWeight: FONT_WEIGHT.bold,
                      color: snapshotStatusColor, letterSpacing: LETTER_SPACING.label,
                    }}>{zoneSnapshotStatusLabel(snapshotStatus)}</span>
                  </div>
                )}
              </div>

              {/* Current live density — shown when live snapshot available */}
              {currentSnapshot && (
                <div style={{ marginBottom: SPACE[12] }}>
                  <div style={{ fontSize: FONT_SIZE.base, color: C.muted, marginBottom: SPACE[4] }}>Current density</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: SPACE[8] }}>
                    <span style={{ fontSize: FONT_SIZE.metricLg, fontWeight: FONT_WEIGHT.black, color: liveColor(currentSnapshot.capacityUtilisation, C), lineHeight: LINE_HEIGHT.tight }}>
                      {currentSnapshot.capacityUtilisation}%
                    </span>
                    <span style={{ fontSize: FONT_SIZE.xs, color: C.muted }}>
                      trigger: {snap.capacityUtilisation}%
                    </span>
                  </div>
                  <div style={{ height: SIZE.barLg, background: C.faint, borderRadius: RADIUS.sm, marginTop: SPACE[8], overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.min(currentSnapshot.capacityUtilisation, 100)}%`, background: liveColor(currentSnapshot.capacityUtilisation, C), transition: 'width 600ms ease' }} />
                  </div>
                </div>
              )}

              {/* Historical density at trigger — shown when no live snapshot */}
              {!currentSnapshot && (
                <div style={{ marginBottom: SPACE[12] }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: SPACE[8], marginBottom: SPACE[4] }}>
                    <div style={{ fontSize: FONT_SIZE.base, color: C.muted }}>Density at trigger</div>
                    <span style={{
                      fontSize: FONT_SIZE.label, fontWeight: FONT_WEIGHT.bold,
                      color: snapshotStatusColor, letterSpacing: LETTER_SPACING.label,
                    }}>{zoneSnapshotStatusLabel(snapshotStatus)}</span>
                  </div>
                  <div style={{ fontSize: FONT_SIZE.metricLg, fontWeight: FONT_WEIGHT.black, color: sevColor, lineHeight: LINE_HEIGHT.tight }}>
                    {snap.capacityUtilisation}%
                  </div>
                  <div style={{ height: SIZE.barLg, background: C.faint, borderRadius: RADIUS.sm, marginTop: SPACE[8], overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.min(snap.capacityUtilisation, 100)}%`, background: sevColor }} />
                  </div>
                </div>
              )}

              {([
                ['Occupancy',    currentSnapshot ? `${currentSnapshot.occupancyCount.toLocaleString()} persons` : `${snap.occupancyCount.toLocaleString()} persons`],
                ['Capacity use', `${(currentSnapshot ?? snap).capacityUtilisation}%`],
                ['Trend',        currentSnapshot ? `${currentSnapshot.trendDirection} ${currentSnapshot.trendRate > 0 ? `+${currentSnapshot.trendRate.toFixed(1)}` : currentSnapshot.trendRate.toFixed(1)}%/min` : `+${snap.surgeRate}% / 5m`],
              ] as [string, string][]).map(([label, value]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: `${SPACE[4]}px 0` }}>
                  <span style={{ fontSize: FONT_SIZE.base, color: C.muted }}>{label}</span>
                  <span style={{ fontSize: FONT_SIZE.basePlus, fontWeight: FONT_WEIGHT.semibold, color: C.text }}>{value}</span>
                </div>
              ))}
            </Panel>
          </div>

          {/* ── Resolution note (supervisors only, unresolved alerts) ───── */}
          {canResolve && alert.status !== 'resolved' && (
            <Panel C={C} style={{ padding: SPACE[18], marginBottom: SPACE[16] }}>
              <KLabel C={C}>Resolution Note</KLabel>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value.slice(0, RESOLUTION_NOTE_MAX_LENGTH))}
                placeholder="Optional resolution note — crowd dispersed, team deployed, false positive, etc."
                rows={3}
                style={{
                  width: '100%', padding: `${SPACE[8]}px ${SPACE[10]}px`,
                  fontSize: FONT_SIZE.body,
                  background: C.surface, color: C.text,
                  border: `1px solid ${C.border2}`, borderRadius: RADIUS.md,
                  fontFamily: "'Albert Sans', sans-serif",
                  outline: 'none', resize: 'vertical',
                  minHeight: SIZE.textareaMin, boxSizing: 'border-box',
                }}
              />
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginTop: SPACE[8],
              }}>
                <span style={{ fontSize: FONT_SIZE.label, color: C.muted, fontFamily: 'monospace' }}>
                  {note.length} / {RESOLUTION_NOTE_MAX_LENGTH}
                </span>
                {note.length > 0 && noteValidationError && (
                  <span style={{ fontSize: FONT_SIZE.label, color: C.ember }}>
                    {noteValidationError}
                  </span>
                )}
                <Btn
                  C={C} variant="success" size="sm"
                  icon={<Icons.Check size={SIZE.iconSm} />}
                  onClick={handleRes} disabled={!canSubmitResolution}
                >
                  {resLoading ? 'Resolving…' : 'Mark Resolved'}
                </Btn>
              </div>
            </Panel>
          )}

          {/* ── Resolved state display ───────────────────────────────────── */}
          {alert.status === 'resolved' && alert.resolvedAt && (
            <Panel C={C} style={{ padding: SPACE[18], marginBottom: SPACE[16] }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[8], marginBottom: SPACE[10] }}>
                <Icons.Check size={SIZE.iconXl} />
                <span style={{ fontSize: FONT_SIZE.sub, fontWeight: FONT_WEIGHT.bold, color: C.green }}>
                  Resolved
                </span>
                <span style={{ fontSize: FONT_SIZE.sm, color: C.muted, fontFamily: 'monospace' }}>
                  at {formatTime(alert.resolvedAt)}
                  {alert.resolvedBy ? ` · by ${truncateId(alert.resolvedBy)}` : ''}
                </span>
              </div>
              {alert.resolutionNote && (
                <div style={{
                  fontSize: FONT_SIZE.body, color: C.sub, lineHeight: LINE_HEIGHT.relaxed,
                  background: C.faint, borderRadius: RADIUS.md,
                  padding: `${SPACE[10]}px ${SPACE[12]}px`,
                  borderLeft: `${SIZE.severityBar}px solid ${C.green}`,
                }}>
                  {alert.resolutionNote}
                </div>
              )}
            </Panel>
          )}
        </>
      )}

      {tab === 'history' && (
        /* ── History: full action log ──────────────────────────────────── */
        <Panel C={C} style={{ padding: SPACE[18] }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: SPACE[12], marginBottom: SPACE[10] }}>
            <KLabel C={C} mb={0}>Alert Timeline</KLabel>
            {history.retentionMonths !== null && (
              <span style={{ fontSize: FONT_SIZE.label, color: C.muted, fontFamily: 'monospace' }}>
                {history.retentionMonths} month retention
              </span>
            )}
          </div>
          {history.loading ? (
            <div style={{ fontSize: FONT_SIZE.body, color: C.muted }}>Loading lifecycle history...</div>
          ) : history.error ? (
            <div style={{ fontSize: FONT_SIZE.body, color: C.ember }}>{history.error}</div>
          ) : history.entries.length > 0 ? (
            history.entries.map((e, i) => (
              <ActionEntry
                key={`${e.timestamp}-${e.action}-${i}`}
                time={formatTime(e.timestamp)}
                who={e.actorLabel}
                action={e.action}
                text={e.note}
                C={C}
              />
            ))
          ) : (
            <div style={{ fontSize: FONT_SIZE.body, color: C.muted }}>No lifecycle history returned for this alert.</div>
          )}
        </Panel>
      )}
    </div>
  );
}

// ── Empty detail placeholder ──────────────────────────────────────────────────

function EmptyDetail({ C }: { C: ColorPalette }) {
  return (
    <div style={{
      height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Albert Sans', sans-serif",
    }}>
      <div style={{ textAlign: 'center', color: C.muted }}>
        <Icons.Bell size={SIZE.iconXxl} />
        <div style={{
          fontSize: FONT_SIZE.sub, fontWeight: FONT_WEIGHT.semibold,
          color: C.sub, marginTop: SPACE[12],
        }}>
          Select an alert to view details
        </div>
        <div style={{ fontSize: FONT_SIZE.base, color: C.muted, marginTop: SPACE[4] }}>
          Alerts arrive live as they are triggered
        </div>
      </div>
    </div>
  );
}

// ── Screen ────────────────────────────────────────────────────────────────────

type AlertFilterStatus = PagedAlertFilterStatus;

const FILTER_TABS: Array<{ id: AlertFilterStatus; label: string }> = [
  { id: 'active',       label: 'Active'       },
  { id: 'open',         label: 'Open'         },
  { id: 'acknowledged', label: 'Acknowledged' },
  { id: 'escalated',    label: 'Escalated'    },
  { id: 'resolved',     label: 'Resolved'     },
  { id: 'all',          label: 'All'          },
];

export function AlertsScreen() {
  const {
    theme,
    currentUser,
    selectedOperation,
    selectedOperationAlerts,
    alertStreamConnected,
    alertStreamConnectionStatus,
    alertStreamSource,
    alertStreamLastEventAt,
    alertStreamLastSyncAt,
    alertStreamError,
    selectedOperationAlertSummary,
    acknowledgeAlert,
    escalateAlert,
    resolveAlert,
  } = useApp();
  const C = getCrowdC(theme);
  const recordActivity = useCrowdActivityAudit();

  const { isMock } = useMockMode();
  const densityData = useDensityData();
  const { zones } = useZones();
  const liveZoneMetrics = useLiveZoneMetrics(selectedOperation?.id ?? null, Boolean(!isMock && selectedOperation?.status === 'active'), 2000);

  const alerts = selectedOperation
    ? selectedOperationAlerts.filter(a => selectedOperation.assignedZoneIds.includes(a.zoneId))
    : [];

  const [selectedId,   setSelectedId]   = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<AlertFilterStatus>('active');
  const [, setTick] = useState(0);
  const pagedAlerts = usePagedAlerts(
    selectedOperation?.id ?? null,
    filterStatus,
    Boolean(!isMock && selectedOperation),
    selectedOperation?.assignedZoneIds ?? [],
  );
  useCrowdFilterActivity({
    page: '/alerts',
    operationId: selectedOperation?.id,
    filterValues: {
      status: filterStatus,
    },
  });

  const { toasts, show: showToast, dismiss: dismissToast } = useToast();

  // Drive elapsed timer updates every second.
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const canResolve = currentUser.role === 'system_admin' || currentUser.role === 'command_supervisor';
  const operationStatus = selectedOperation?.status ?? null;
  const operationColor = operationStatusColor(C, operationStatus ?? undefined);
  const assignedZoneCount = selectedOperation?.assignedZoneIds.length ?? 0;
  const assignedZoneIds = useMemo(
    () => new Set(selectedOperation?.assignedZoneIds ?? []),
    [selectedOperation],
  );
  const operationZoneNameById = useMemo(
    () => new Map(
      zones
        .filter(zone => assignedZoneIds.has(zone.id))
        .map(zone => [zone.id, zone.name.trim()] as const)
        .filter(([, name]) => name.length > 0),
    ),
    [assignedZoneIds, zones],
  );
  const selectedOperationZoneMetrics = useMemo(
    () => liveZoneMetrics.rows.filter(row =>
      row.operationId === selectedOperation?.id && assignedZoneIds.has(row.zoneId),
    ),
    [assignedZoneIds, liveZoneMetrics.rows, selectedOperation?.id],
  );
  const returnedZoneMetricsByZone = useMemo(
    () => Object.fromEntries(selectedOperationZoneMetrics.map(row => [row.zoneId, row])),
    [selectedOperationZoneMetrics],
  );
  const freshZoneMetricsByZone = useMemo(
    () => Object.fromEntries(selectedOperationZoneMetrics.filter(row => isZoneMetricFresh(row)).map(row => [row.zoneId, row])),
    [selectedOperationZoneMetrics],
  );

  const filtered = alerts.filter(a => {
    if (filterStatus === 'active') return a.status !== 'resolved';
    if (filterStatus === 'all') return true;
    return a.status === filterStatus;
  });
  const visibleAlerts = isMock
    ? filtered
    : pagedAlerts.alerts.filter(a => assignedZoneIds.has(a.zoneId));
  const alertPagination = isMock
    ? {
      total: filtered.length,
      page: 1,
      pageSize: Math.max(filtered.length, 1),
      hasNextPage: false,
    }
    : pagedAlerts.pagination;

  // When the list arrives, open the first alert of the list that is shown, so the detail beside the list always belongs
  // to one of its rows (the newest alert overall may already be resolved and not be in the list at all).
  useEffect(() => {
    if (!selectedId && visibleAlerts.length > 0) setSelectedId(visibleAlerts[0].id);
  }, [visibleAlerts, selectedId]);

  const selectedAlert = visibleAlerts.find(a => a.id === selectedId)
    ?? alerts.find(a => a.id === selectedId)
    ?? null;

  const counts = {
    open: selectedOperationAlertSummary.open,
    ack:  selectedOperationAlertSummary.acknowledged,
    esc:  selectedOperationAlertSummary.escalated,
    res:  selectedOperationAlertSummary.resolved,
  };
  const countsUnavailable = !isMock && alertStreamConnectionStatus === 'disconnected';
  const filteredTotal = filterStatus === 'active'
    ? selectedOperationAlertSummary.active
    : filterStatus === 'all'
      ? selectedOperationAlertSummary.total
      : selectedOperationAlertSummary[filterStatus];
  const streamStatusColor = alertStreamStatusColor(alertStreamConnectionStatus, C);
  const streamStatusLabel = alertStreamStatusLabel(alertStreamConnectionStatus, alertStreamSource);
  const streamSyncLabel = formatStreamSyncTime(alertStreamLastSyncAt ?? alertStreamLastEventAt);
  const streamSourceLabel = alertStreamSource === 'none'
    ? 'No source'
    : alertStreamSource === 'core' ? 'Live source' : `${formatType(alertStreamSource)} source`;

  useEffect(() => {
    pagedAlerts.setPage(1);
  }, [filterStatus, pagedAlerts.setPage, selectedOperation?.id]);

  // The paged list is a one-shot REST read, so alerts raised or auto-escalated after it loaded would be missing
  // until a manual action. Re-read it whenever the live stream's per-status counts move.
  const streamCountsKey = [
    selectedOperationAlertSummary.total,
    selectedOperationAlertSummary.open,
    selectedOperationAlertSummary.acknowledged,
    selectedOperationAlertSummary.escalated,
    selectedOperationAlertSummary.resolved,
  ].join('|');
  const lastStreamCountsKeyRef = useRef<string | null>(null);
  const refreshPagedAlerts = pagedAlerts.refresh;
  useEffect(() => {
    if (isMock) return;
    if (lastStreamCountsKeyRef.current !== null && lastStreamCountsKeyRef.current !== streamCountsKey) {
      refreshPagedAlerts();
    }
    lastStreamCountsKeyRef.current = streamCountsKey;
  }, [isMock, refreshPagedAlerts, streamCountsKey]);

  // Lifecycle action handlers with toast feedback.
  const handleAcknowledge = useCallback(async (id: string) => {
    try {
      await acknowledgeAlert(id);
      pagedAlerts.refresh();
      showToast('Alert acknowledged.', 'success');
    } catch {
      showToast('Failed to acknowledge — check connection.', 'error');
    }
  }, [acknowledgeAlert, pagedAlerts, showToast]);

  const handleEscalate = useCallback(async (id: string) => {
    try {
      await escalateAlert(id);
      pagedAlerts.refresh();
      showToast('Alert escalated to Command.', 'warning');
    } catch {
      showToast('Failed to escalate — check connection.', 'error');
    }
  }, [escalateAlert, pagedAlerts, showToast]);

  const handleResolve = useCallback(async (id: string, note: string) => {
    try {
      await resolveAlert(id, note.trim());
      pagedAlerts.refresh();
      showToast('Alert resolved.', 'success');
    } catch {
      showToast('Failed to resolve — check connection.', 'error');
    }
  }, [pagedAlerts, resolveAlert, showToast]);

  const selectAlert = useCallback((alert: Alert) => {
    setSelectedId(alert.id);
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.recordViewed,
      page: '/alerts',
      operationId: selectedOperation?.id,
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.alert,
      resourceId: alert.id,
      metadata: { sourcePage: '/alerts' },
    });
  }, [recordActivity, selectedOperation?.id]);

  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden', fontFamily: "'Albert Sans', sans-serif" }}>

      {/* ── Left: alert stream ───────────────────────────────────────────── */}
      <div style={{
        width: SIZE.alertColumn, flexShrink: 0,
        borderRight: `1px solid ${C.border2}`,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>

        {/* Header + count grid */}
        <div style={{ padding: `${SPACE[14]}px ${SPACE[18]}px`, borderBottom: `1px solid ${C.border2}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: SPACE[10] }}>
            <div style={{ fontSize: FONT_SIZE.sub, fontWeight: FONT_WEIGHT.bold, color: C.text }}>
              Alert Stream
            </div>
            <div style={{ fontSize: FONT_SIZE.xs, color: C.muted, fontFamily: 'monospace' }}>
              {filteredTotal} of {selectedOperationAlertSummary.total}
            </div>
          </div>
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: SPACE[8],
            padding: `${SPACE[8]}px ${SPACE[10]}px`, marginBottom: SPACE[10],
            background: C.faint, border: `1px solid ${C.border}`,
            borderRadius: RADIUS.md,
          }}>
            <StatusDot color={operationColor} pulse={operationStatus === 'active'} size={6} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                fontSize: FONT_SIZE.label, color: C.muted, fontWeight: FONT_WEIGHT.bold,
                letterSpacing: LETTER_SPACING.label, textTransform: 'uppercase',
              }}>
                Selected Operation
              </div>
              {selectedOperation ? (
                <>
                  <div style={{
                    fontSize: FONT_SIZE.base, color: C.text,
                    fontWeight: FONT_WEIGHT.semibold, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {selectedOperation.name}
                  </div>
                  <div style={{ fontSize: FONT_SIZE.sm, color: C.muted, marginTop: SPACE[2] }}>
                    <span style={{ color: operationStatusColor(C, selectedOperation.status), fontWeight: FONT_WEIGHT.bold }}>
                      {formatType(selectedOperation.status)}
                    </span> - {assignedZoneCount} assigned zones
                  </div>
                </>
              ) : (
                <div style={{ fontSize: FONT_SIZE.sm, color: C.muted, marginTop: SPACE[2] }}>
                  Select an Operation to view its alert stream.
                </div>
              )}
            </div>
          </div>
          {!isMock && alertStreamConnectionStatus === 'stale' && (
            <div style={{
              fontSize: FONT_SIZE.sm, color: C.amber, marginBottom: SPACE[6],
            }}>
              Alert stream is stale. Counts below are last known.
            </div>
          )}
          {/* Two by two, so the four states are spelled out in full, as they are in the lifecycle and the filters. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: SPACE[6] }}>
            <CountChip label="Open"         value={countsUnavailable ? null : counts.open} color={C.ember}    C={C} />
            <CountChip label="Acknowledged" value={countsUnavailable ? null : counts.ack}  color={C.amber}    C={C} />
            <CountChip label="Escalated"    value={countsUnavailable ? null : counts.esc}  color={C.critical} C={C} />
            <CountChip label="Resolved"     value={countsUnavailable ? null : counts.res}  color={C.green}    C={C} />
          </div>
        </div>

        {/* Filter tabs */}
        <div style={{
          padding: `${SPACE[10]}px ${SPACE[18]}px`,
          borderBottom: `1px solid ${C.border}`,
          display: 'flex', gap: SPACE[4], flexWrap: 'wrap',
        }}>
          {FILTER_TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setFilterStatus(t.id)}
              style={{
                fontSize: FONT_SIZE.base, fontWeight: FONT_WEIGHT.semibold,
                padding: `${SPACE[5]}px ${SPACE[10]}px`, borderRadius: RADIUS.sm,
                background: filterStatus === t.id ? C.amberDim : 'transparent',
                border: `1px solid ${filterStatus === t.id ? `${C.amber}55` : C.border2}`,
                color:  filterStatus === t.id ? C.amber : C.sub,
                cursor: 'pointer', fontFamily: "'Albert Sans', sans-serif",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Stream list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {visibleAlerts.length === 0 ? (
            <div style={{ padding: `40px ${SPACE[18]}px`, textAlign: 'center' }}>
              <Icons.Bell size={SIZE.iconXxl} />
              <div style={{ fontSize: FONT_SIZE.body, color: C.muted, marginTop: SPACE[8] }}>
                {selectedOperationAlertSummary.total === 0
                  ? 'Waiting for alerts from the event stream…'
                  : 'No alerts match this filter.'}
              </div>
            </div>
          ) : visibleAlerts.map(a => (
            <AlertRow
              key={a.id}
              alert={a}
              zoneDisplayName={alertZoneDisplayName(a, operationZoneNameById)}
              isSelected={a.id === selectedId}
              onClick={() => selectAlert(a)}
              C={C}
            />
          ))}
        </div>

        {/* Connection footer */}
        <PaginationControls
          C={C}
          pagination={alertPagination}
          loading={isMock ? alertStreamConnectionStatus === 'connecting' : pagedAlerts.loading}
          error={pagedAlerts.error ?? (alertStreamConnectionStatus === 'disconnected' ? alertStreamError : null)}
          emptyLabel={selectedOperationAlertSummary.total === 0 ? 'Waiting for alerts...' : 'No alerts match this filter.'}
          onPageChange={isMock ? () => undefined : pagedAlerts.setPage}
          onPageSizeChange={isMock ? () => undefined : pagedAlerts.setPageSize}
        />
        <div style={{ padding: `${SPACE[10]}px ${SPACE[18]}px`, borderTop: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: SPACE[8] }}>
            <StatusDot color={streamStatusColor} pulse={alertStreamConnected} size={5} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                fontSize: FONT_SIZE.sm,
                color: streamStatusColor,
                fontWeight: FONT_WEIGHT.semibold,
              }}>
                {streamStatusLabel}
              </div>
              <div style={{ fontSize: FONT_SIZE.label, color: C.muted, marginTop: SPACE[2] }}>
                {streamSourceLabel} - {streamSyncLabel}
              </div>
              {alertStreamError && alertStreamConnectionStatus !== 'connected' && (
                <div style={{ fontSize: FONT_SIZE.label, color: C.ember, marginTop: SPACE[2] }}>
                  {alertStreamError}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Right: alert detail ──────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: SPACE[24] }}>
        {selectedAlert && selectedOperation ? (
          <AlertDetail
            alert={selectedAlert}
            zoneDisplayName={alertZoneDisplayName(selectedAlert, operationZoneNameById)}
            operationId={selectedOperation.id}
            canResolve={canResolve}
            isSimulation={isMock}
            simulationSnapshot={isMock ? densityData.snapshots[selectedAlert.zoneId] ?? null : null}
            selectedZoneMetric={freshZoneMetricsByZone[selectedAlert.zoneId] ?? null}
            returnedZoneMetric={returnedZoneMetricsByZone[selectedAlert.zoneId] ?? null}
            zoneMetricsLoading={liveZoneMetrics.loading}
            zoneMetricsError={liveZoneMetrics.error}
            onAcknowledge={() => handleAcknowledge(selectedAlert.id)}
            onEscalate={()    => handleEscalate(selectedAlert.id)}
            onResolve={(note) => handleResolve(selectedAlert.id, note)}
            onTabChange={(tab) => recordActivity({
              action: CROWD_CLIENT_ACTIVITY_ACTION.tabChanged,
              page: '/alerts',
              operationId: selectedOperation.id,
              resourceType: CROWD_CLIENT_RESOURCE_TYPE.uiTab,
              resourceId: 'alert-detail',
              metadata: { tab, sourcePage: '/alerts' },
            })}
            C={C}
          />
        ) : (
          <EmptyDetail C={C} />
        )}
      </div>

      {/* ── Toast overlay ────────────────────────────────────────────────── */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} C={C} />
    </div>
  );
}
